import { Auth } from 'aws-amplify';
import * as moment from 'moment';

import { showError, showSuccess } from '../common/alerts/alerts';
import Logger from '../../utility/services/Logger';
import userInfoController from '../../utility/userInfoController';
import WiseKoinAmount from '../../constants/WiseKoinAmount';
import getToken from '../../utility/getToken';
import NotificationController from '../../utility/notificationController';
import getRegionPayment from '../../utility/getRegionPayment';

export const getUserIdAndToken = async (props) => {
  const token = await getToken();
  const userId = props?.user?.profile?.sub;
  const firstName = props?.user?.profile?.name;
  return {
    token,
    userId,
    firstName,
  };
};

export const handleLoginCatchGA4 = (err) => {
  Logger.ClearUserLogScope();
  switch (err.code) {
    case 'UserNotFoundException':
      showError('Incorrect username or password.', false);
      break;
    case 'NotAuthorizedException':
      if (err.message === 'User is disabled') {
        showError(err.message, false);
      } else if (err.message === 'Incorrect username or password.') {
        showError(err.message, false);
      } else {
        Logger.LogError(err);
        showError("Something happened and I couldn't sign you in", false);
      }
      break;
    case 'PreSignUp_AccountNotFount':
      if (err.message.includes('Account is setup')) showSuccess(err.message);
      else showError(err.message, false);
      break;
    default:
      Logger.LogError(err);
      showError("Something happened and I couldn't sign you in", false);
      break;
  }
};

export const signInUser = async (user, updateUser) => {
  const currentUserInfo = await Auth.currentUserInfo();
  updateUser(user, currentUserInfo.attributes);
  Logger.SetUserLogScope(user.attributes.sub);
  return {
    user: { user, profile: currentUserInfo.attributes },
  };
};

export const handleFederatedLogin = async (updateUser, error) => {
  try {
    if (error) {
      throw error;
    }
    const user = await Auth.currentAuthenticatedUser();
    const data = signInUser(user, updateUser);
    return data;
  } catch (err) {
    handleLoginCatchGA4(err);
    return null;
  }
};

export const PostSignInCalls = async (props) => {
  const { getUserInfo, addNotifications, user } = props;
  const {
    attributes: { sub: userId },
    signInUserSession: {
      idToken: { jwtToken: token },
    },
  } = user;
  localStorage.setItem('cuid', userId);
  await getUserInfo(userId, token);
  const response = await NotificationController.getNotifications(token);
  addNotifications(response.data);
};

export const handleLogin = async (email, password, signIn, signup) => {
  try {
    const user = await Auth.signIn(email, password);
    const currentHref = window.location.href;
    if (signup) {
      window.location.href = `${currentHref}?signup=true`;
    } else {
      window.location.href = currentHref;
    }
    // filter attributes
    const data = await signInUser(user, signIn);
    return data;
  } catch (err) {
    handleLoginErrors(err);
    return null;
  }
};

const handleLoginErrors = (err) => {
  Logger.ClearUserLogScope();
  switch (err.code) {
    case 'UserNotFoundException':
      throw new Error('Incorrect username or password.');
    case 'NotAuthorizedException':
      if (err.message === 'User is disabled') {
        throw new Error(err.message);
      } else if (err.message === 'Incorrect username or password.') {
        throw new Error(err.message);
      } else {
        Logger.LogError(err);
        throw new Error("Something happened and I couldn't sign you in");
      }
    case 'PreSignUp_AccountNotFount':
      if (err.message.includes('Account is setup')) showSuccess(err.message);
      else throw new Error(err.message);
      break;
    default:
      Logger.LogError(err);
      throw new Error("Something happened and I couldn't sign you in");
  }
};

export const checkFirstSignIn = async (props) => {
  const { token, userId } = await getUserIdAndToken(props);
  const region = await getRegionPayment({ appType: 'CRA' });
  const userInfo = await userInfoController.getUserInfoById(
    userId,
    token,
    region,
  );
  const { isFirstSignIn } = userInfo;
  if (isFirstSignIn || !userInfo) {
    return true;
  }
  return false;
};

export const getUserInfoByUserName = async (userName, token) => {
  const userInfo = await userInfoController.getUserInfoByUserName(
    userName,
    token,
  );
  return userInfo;
};

export const addBonusToSubAndRef = async (props) => {
  const {
    user: { profile },
  } = props;

  const { 'custom:referrerId': referrerId } = profile;

  const fullName = `${profile.name} ${profile.family_name}`;

  const { token, userId } = await getUserIdAndToken(props);
  if (referrerId) {
    const date = moment().toISOString();
    const referredUserInfo = {
      userId,
      signUpBonus: WiseKoinAmount.SIGNUP_AMOUNT,
      subscriptionBonus: 0,
      bonusDate: date,
      name: fullName,
    };
    const { partitionKey: referrerUserId } = await getUserInfoByUserName(
      referrerId,
      token,
    );
    const data = {
      type: { name: 'firstSignIn', value: WiseKoinAmount.SIGNUP_AMOUNT },
      wiseKoin: WiseKoinAmount.SIGNUP_AMOUNT,
      referrerId: referrerUserId,
      referredUser: referredUserInfo,
      isFirstSignIn: false,
      isFirstSubscription: true,
      totalEarned: WiseKoinAmount.SIGNUP_AMOUNT,
    };
    await userInfoController.addBonusToSubAndRef(userId, data, token);
  }
};

export const getUserName = async (key, token) => {
  const { userNames } = await userInfoController.getUserName(key, token);
  return userNames;
};

export const userNameExist = async (userNames, firstName) => {
  if (userNames.length > 0) {
    const reg = `(${firstName})`;
    const re = new RegExp(reg);
    const filteredUserName = userNames.filter((existingUserName) =>
      existingUserName.match(re),
    );
    return filteredUserName;
  }
  return [];
};

export const GenerateUserName = async (props) => {
  const { token, firstName } = await getUserIdAndToken(props);
  const splitFirstName = firstName.split(' ');
  let uniqueUserName = splitFirstName[splitFirstName.length - 1].toLowerCase();
  const key = uniqueUserName.charAt(0);
  const userNames = (await getUserName(key, token)) || [];
  const existingUserNames = await userNameExist(userNames, uniqueUserName);
  if (existingUserNames.length > 0) {
    const numbers = existingUserNames.map((name) => name.match(/[0-9]+/));
    numbers.sort((a, b) => b - a);
    const lastNumber = Number(numbers[0]);
    uniqueUserName = `${uniqueUserName}${lastNumber + 1}`;
  }
  const newUserNames = [...userNames, uniqueUserName];
  await userInfoController.InsertUserName(key, newUserNames, token);
  return uniqueUserName;
};
